/**
 * FIX ORPHAN CATALOG (Admin Tool)
 *
 * Scans polishCatalog for entries whose brandSlug doesn't match any
 * current brand doc (orphans), groups them by brandSlug, and lets the
 * admin pick a canonical target brand per group. Applying the fix
 * renames the underlying user items' brand field — the catalog triggers
 * then rebuild the catalog automatically, deleting the orphan slug
 * entries and creating new ones under the canonical brand.
 *
 * Why this works:
 *   - polishCatalog entries are derived from user items via the
 *     onUserPolishWrite trigger
 *   - An orphan catalog slug means user items exist with a brand string
 *     that slugifies to something with no matching brand doc
 *   - Renaming those items' brand field to a canonical brand fixes
 *     everything downstream automatically
 *
 * REQUIRES: isAdmin: true.
 * Route: /admin/fix-orphan-catalog
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  collectionGroup,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  writeBatch,
  addDoc,
  serverTimestamp,
  orderBy,
  limit as fsLimit,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft } from 'lucide-react';
import { slugify } from '../utils/polishSlug';
import type { CurationStatus } from '../types/brand';

interface BrandRow {
  id: string;
  name: string;
  slug: string;
  curationStatus: CurationStatus | null;
}

interface OrphanGroup {
  brandSlug: string;
  count: number;
  sampleNames: string[]; // polish name fragments to show context
  brandDisplayStrings: string[]; // distinct values of catalogDoc.brand
}

type FixStatus = 'pending' | 'running' | 'done' | 'error';

interface PreviewResult {
  itemCount: number;
  samples: Array<{ owner: string; brand: string; polishName: string }>;
  variants: string[];
}

interface AuditLogEntry {
  id: string;
  timestampMs: number | null;
  fromOrphanSlug?: string;
  toBrandName?: string;
  toBrandSlug?: string;
  itemCount?: number;
  fromBrandStrings?: string[];
  adminUid?: string;
}

/** Build the obvious case variants of a brand string so item rename queries
 *  catch every capitalization variant. Mirrors MergeBrandDocs.tsx. */
function buildCaseVariants(name: string): string[] {
  const seen = new Set<string>();
  const push = (s: string) => {
    if (s) seen.add(s);
  };
  push(name);
  push(name.trim());
  push(name.toLowerCase());
  push(name.toUpperCase());
  push(
    name
      .toLowerCase()
      .replace(/\b([a-z])/g, (m) => m.toUpperCase())
  );
  return Array.from(seen);
}

const CURATION_RANK: Record<string, number> = {
  verified: 0,
  community: 1,
  'needs-review': 2,
};
function curationRank(b: BrandRow): number {
  return CURATION_RANK[b.curationStatus ?? 'community'] ?? 1;
}

export default function FixOrphanCatalog() {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  const [auditing, setAuditing] = useState(false);
  const [audited, setAudited] = useState(false);
  const [orphans, setOrphans] = useState<OrphanGroup[]>([]);
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [auditError, setAuditError] = useState<string | null>(null);

  // Per-orphan target brand selection (orphanSlug → brandId) and per-orphan
  // status (pending/running/done/error) so the admin can apply one at a time.
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [statuses, setStatuses] = useState<Record<string, FixStatus>>({});
  const [logs, setLogs] = useState<Record<string, string>>({});
  // Dry-run preview per orphan — same query the rename would do, but
  // collects results instead of writing. Lets admin verify item count
  // + sample contents before committing.
  const [previews, setPreviews] = useState<Record<string, PreviewResult>>({});
  const [previewing, setPreviewing] = useState<Record<string, boolean>>({});

  // Persistent audit log of recent renames (from brandRenameLog collection).
  // Each Rename action writes one entry; the latest 25 are loaded on mount
  // and after every successful rename so the admin sees a running history.
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Persistent skip list — stored in localStorage so skipped orphans
  // don't reappear on every re-audit. Reset via "Clear skipped" button.
  const SKIPPED_KEY = 'fix-orphan-catalog-skipped';
  const [skippedSlugs, setSkippedSlugs] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(SKIPPED_KEY);
      if (!stored) return new Set();
      const arr = JSON.parse(stored) as string[];
      return new Set(arr);
    } catch {
      return new Set();
    }
  });
  const persistSkip = (slug: string) => {
    setSkippedSlugs((prev) => {
      const next = new Set(prev);
      next.add(slug);
      try {
        localStorage.setItem(SKIPPED_KEY, JSON.stringify(Array.from(next)));
      } catch {}
      return next;
    });
  };
  const clearSkips = () => {
    setSkippedSlugs(new Set());
    try {
      localStorage.removeItem(SKIPPED_KEY);
    } catch {}
  };

  // Search filter per orphan group for the brand picker.
  const [brandFilters, setBrandFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(userDoc.exists() && userDoc.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return unsubscribe;
  }, []);

  /** Run the audit: load all brand slugs and all polishCatalog docs, find
   *  catalog entries whose brandSlug isn't in the live set, group by
   *  brandSlug. Costs ~34k Firestore reads — fine for a one-shot. */
  const runAudit = async () => {
    setAuditing(true);
    setAuditError(null);
    setOrphans([]);
    setStatuses({});
    setLogs({});
    setTargets({});
    setBrandFilters({});

    try {
      // 1. Load all brands. Track both id and slug.
      const brandsSnap = await getDocs(collection(db, 'brands'));
      const liveSlugs = new Set<string>();
      const brandList: BrandRow[] = [];
      brandsSnap.forEach((d) => {
        const data = d.data();
        const slug = (data.slug as string) || '';
        if (slug) liveSlugs.add(slug);
        const name = (data.name as string) || '';
        if (name) {
          brandList.push({
            id: d.id,
            name,
            slug,
            curationStatus:
              (data.curationStatus as CurationStatus | undefined) ?? null,
          });
        }
      });
      brandList.sort((a, b) => {
        // verified first, then alphabetical
        const r = curationRank(a) - curationRank(b);
        return r !== 0 ? r : a.name.localeCompare(b.name);
      });
      setBrands(brandList);

      // 2. Walk polishCatalog, group orphans by brandSlug.
      const catalogSnap = await getDocs(collection(db, 'polishCatalog'));
      const grouped = new Map<string, OrphanGroup>();
      catalogSnap.forEach((d) => {
        const data = d.data();
        const brandSlug = (data.brandSlug as string) || '';
        if (!brandSlug) return;
        if (liveSlugs.has(brandSlug)) return;

        let g = grouped.get(brandSlug);
        if (!g) {
          g = {
            brandSlug,
            count: 0,
            sampleNames: [],
            brandDisplayStrings: [],
          };
          grouped.set(brandSlug, g);
        }
        g.count++;
        if (g.sampleNames.length < 5) {
          g.sampleNames.push((data.name as string) || '(no name)');
        }
        const brandStr = (data.brand as string) || '';
        if (brandStr && !g.brandDisplayStrings.includes(brandStr)) {
          g.brandDisplayStrings.push(brandStr);
        }
      });

      // Filter out persistently-skipped slugs so they don't reappear on
      // re-audit. Surface the count in the header so admin knows.
      const allOrphans = Array.from(grouped.values()).sort(
        (a, b) => b.count - a.count
      );
      setOrphans(allOrphans.filter((o) => !skippedSlugs.has(o.brandSlug)));
      setAudited(true);
    } catch (err: any) {
      setAuditError(err?.message ?? String(err));
    } finally {
      setAuditing(false);
    }
  };

  /** Load the latest 25 rename log entries. Called on mount and after
   *  every successful rename so the admin sees their actions in real time
   *  and on subsequent visits. Silent on failure (rules might not be
   *  deployed yet). */
  const loadAuditLog = async () => {
    setLoadingAudit(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, 'brandRenameLog'),
          orderBy('timestamp', 'desc'),
          fsLimit(25)
        )
      );
      const entries: AuditLogEntry[] = snap.docs.map((d) => {
        const data = d.data() as any;
        const ts = data.timestamp;
        return {
          id: d.id,
          timestampMs:
            ts && typeof ts.toMillis === 'function' ? ts.toMillis() : null,
          fromOrphanSlug: data.fromOrphanSlug,
          toBrandName: data.toBrandName,
          toBrandSlug: data.toBrandSlug,
          itemCount: data.itemCount,
          fromBrandStrings: data.fromBrandStrings,
          adminUid: data.adminUid,
        };
      });
      setAuditEntries(entries);
    } catch (err: any) {
      // Likely a rules-not-deployed-yet case. Just log to console.
      console.warn('Failed to load audit log (rules?):', err?.message ?? err);
    } finally {
      setLoadingAudit(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadAuditLog();
  }, [isAdmin]);

  /** Build the safe set of brand strings to query for this orphan group.
   *  CRITICAL: we filter brand display strings to ONLY those whose slugify
   *  matches the orphan slug. This prevents cross-contamination — without
   *  the filter, a catalog entry with a mismatched (corrupt) brandSlug
   *  could inject totally unrelated brand strings into the variants set,
   *  causing the rename to touch items from other brands.
   *
   *  Fallback when no brand strings pass the filter: case variants of the
   *  slug itself + slug-with-spaces, so we still catch the "items have
   *  brand=OPI but catalog has empty brand field" case. */
  const variantsForOrphan = (orphan: OrphanGroup): Set<string> => {
    const allVariants = new Set<string>();
    for (const bs of orphan.brandDisplayStrings) {
      // Only use this display string if its slug matches the orphan.
      // Prevents catching items belonging to a totally different brand.
      if (slugify(bs) !== orphan.brandSlug) continue;
      for (const v of buildCaseVariants(bs)) allVariants.add(v);
    }
    // Case variants of the slug as a fallback (catches uppercase variants
    // of items when catalog brand fields are empty).
    for (const v of buildCaseVariants(orphan.brandSlug)) {
      allVariants.add(v);
    }
    // Slug-with-spaces (e.g., "kathleen-and-co" → "Kathleen And Co").
    const spaced = orphan.brandSlug.replace(/-/g, ' ');
    for (const v of buildCaseVariants(spaced)) allVariants.add(v);
    return allVariants;
  };

  /** Detect a "stale" orphan — items already have a correctly-slugged
   *  brand, the catalog entries are just leftover. Renaming items to the
   *  same brand they already have is a no-op for the orphan; the entries
   *  need direct deletion via a Cloud Function (not built yet). We warn
   *  the admin so they don't waste time picking a target. */
  const isStaleOrphan = (orphan: OrphanGroup): boolean => {
    if (orphan.brandDisplayStrings.length === 0) return false;
    // If EVERY observed brand string slugifies to something other than
    // the orphan slug, the catalog is stale (items already moved away).
    return orphan.brandDisplayStrings.every(
      (bs) => slugify(bs) !== orphan.brandSlug
    );
  };

  /** Dry-run: run the same queries `applyOrphan` would run, collect the
   *  unique matched items, surface the count + a few samples. Makes NO
   *  writes. Lets the admin double-check before committing. */
  const previewOrphanFn = async (orphan: OrphanGroup) => {
    setPreviewing((p) => ({ ...p, [orphan.brandSlug]: true }));
    try {
      const allVariants = variantsForOrphan(orphan);
      const seen = new Set<string>();
      const samples: PreviewResult['samples'] = [];

      for (const v of allVariants) {
        const itemsSnap = await getDocs(
          query(collectionGroup(db, 'items'), where('brand', '==', v))
        );
        for (const d of itemsSnap.docs) {
          if (d.ref.parent.parent?.parent.id !== 'users') continue;
          if (seen.has(d.ref.path)) continue;
          seen.add(d.ref.path);
          if (samples.length < 8) {
            const data = d.data() as any;
            samples.push({
              owner: d.ref.parent.parent?.id ?? '?',
              brand: (data.brand as string) || '',
              polishName: (data.name as string) || '(no name)',
            });
          }
        }
      }

      setPreviews((p) => ({
        ...p,
        [orphan.brandSlug]: {
          itemCount: seen.size,
          samples,
          variants: Array.from(allVariants),
        },
      }));
    } finally {
      setPreviewing((p) => ({ ...p, [orphan.brandSlug]: false }));
    }
  };

  /** Rename items belonging to an orphan group → the chosen canonical
   *  brand. We query items by the brand display strings observed in
   *  the catalog (plus their case variants) so we catch every variant.
   *  brand + brandLower get rewritten together. Catalog rebuild is
   *  handled by the existing trigger. */
  const applyOrphan = async (orphan: OrphanGroup) => {
    const targetId = targets[orphan.brandSlug];
    if (!targetId) return;
    const target = brands.find((b) => b.id === targetId);
    if (!target) return;

    setStatuses((s) => ({ ...s, [orphan.brandSlug]: 'running' }));
    setLogs((l) => ({
      ...l,
      [orphan.brandSlug]: `Searching for items via ${orphan.brandDisplayStrings.length} brand strings…`,
    }));

    try {
      const seen = new Set<string>();
      let touched = 0;

      const allVariants = variantsForOrphan(orphan);

      let batch = writeBatch(db);
      let batchOps = 0;
      for (const v of allVariants) {
        const itemsSnap = await getDocs(
          query(collectionGroup(db, 'items'), where('brand', '==', v))
        );
        for (const d of itemsSnap.docs) {
          if (d.ref.parent.parent?.parent.id !== 'users') continue;
          if (seen.has(d.ref.path)) continue;
          seen.add(d.ref.path);
          batch.update(d.ref, {
            brand: target.name,
            brandLower: target.name.toLowerCase(),
          });
          batchOps++;
          touched++;
          if (batchOps >= 400) {
            await batch.commit();
            batch = writeBatch(db);
            batchOps = 0;
          }
        }
      }
      if (batchOps > 0) await batch.commit();

      // Persist an audit log entry so admins can review what was done,
      // long after the session ends. Capture sample paths (capped at 500
      // to stay under the 1MB doc limit) so a reversal is mechanically
      // possible later if needed.
      try {
        const itemPathSamples = Array.from(seen).slice(0, 500);
        await addDoc(collection(db, 'brandRenameLog'), {
          timestamp: serverTimestamp(),
          adminUid: currentUser,
          source: 'fix-orphan-catalog',
          fromOrphanSlug: orphan.brandSlug,
          fromBrandStrings: Array.from(allVariants),
          fromCatalogCount: orphan.count,
          toBrandId: target.id,
          toBrandName: target.name,
          toBrandSlug: target.slug,
          itemCount: touched,
          itemPathsPartial: itemPathSamples,
          itemPathsAreComplete: touched <= 500,
        });
      } catch (logErr: any) {
        // Don't fail the whole rename if log write is denied (e.g., rules
        // not deployed yet). Just warn in the console.
        console.warn('Rename log write failed (rules?):', logErr?.message ?? logErr);
      }

      setStatuses((s) => ({ ...s, [orphan.brandSlug]: 'done' }));
      setLogs((l) => ({
        ...l,
        [orphan.brandSlug]: `✅ Renamed ${touched} items → "${target.name}". Catalog triggers will rebuild momentarily.`,
      }));
      // Refresh the audit log so this rename shows up in the history table.
      loadAuditLog();
    } catch (err: any) {
      setStatuses((s) => ({ ...s, [orphan.brandSlug]: 'error' }));
      setLogs((l) => ({
        ...l,
        [orphan.brandSlug]: `💥 ${err?.message ?? err}`,
      }));
    }
  };

  /** Filtered brand candidates for an orphan's picker. Prefers brands
   *  whose name shares any token with the orphan slug, then any name
   *  matching the user-typed filter. Verified brands rank first. */
  const candidatesFor = useMemo(() => {
    return (orphanSlug: string): BrandRow[] => {
      const filter = (brandFilters[orphanSlug] || '').trim().toLowerCase();
      let list = brands;
      if (filter) {
        list = list.filter(
          (b) =>
            b.name.toLowerCase().includes(filter) ||
            b.slug.toLowerCase().includes(filter)
        );
      } else {
        // Default suggestions: prefer brands whose name or slug contains the
        // orphan slug substring (e.g., orphan "opi" → suggest "O.P.I.").
        list = list.filter(
          (b) =>
            b.slug.toLowerCase().includes(orphanSlug.toLowerCase()) ||
            b.name.toLowerCase().includes(orphanSlug.toLowerCase()) ||
            // Final fallback: if orphan is a single token, suggest brands
            // whose initial letters match.
            slugify(b.name).includes(orphanSlug)
        );
      }
      return list.slice(0, 20);
    };
  }, [brands, brandFilters]);

  if (checkingAdmin) {
    return (
      <div className="max-w-5xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <p className="text-glass-secondary">Checking admin status…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-8">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-4 font-body"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Admin Dashboard
      </Link>

      <div className="glass-card p-6">
        <h1 className="text-2xl font-bold text-glass mb-2 font-heading">
          🩹 Fix Orphan polishCatalog (Admin)
        </h1>
        <p className="text-glass-secondary mb-4 font-body">
          Finds polishCatalog entries whose brand slug doesn&apos;t match any
          existing brand doc, groups them by brand slug, and lets you pick a
          canonical brand to merge each group into. Applies by renaming the
          underlying user items&apos; brand field — catalog triggers do the
          rest.
        </p>

        {!currentUser ? (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-300">⚠️ You must be logged in to use this tool!</p>
          </div>
        ) : !isAdmin ? (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-300">⚠️ You must be an admin to use this tool!</p>
          </div>
        ) : (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
            <p className="text-green-300">✅ Admin access confirmed</p>
          </div>
        )}

        {/* Run audit button */}
        <div className="mb-6">
          <button
            onClick={runAudit}
            disabled={!isAdmin || auditing}
            className={`px-5 py-2.5 rounded-lg font-medium text-white transition-colors font-body ${
              !isAdmin || auditing
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {auditing ? 'Scanning…' : audited ? '🔄 Re-run audit' : '🔎 Run audit'}
          </button>
          {audited && !auditing && (
            <span className="ml-3 text-sm text-glass-muted font-body">
              {orphans.length} orphan groups · {orphans.reduce((s, o) => s + o.count, 0)} total polishCatalog entries
              {skippedSlugs.size > 0 && (
                <>
                  {' · '}
                  <span className="text-yellow-300">{skippedSlugs.size} hidden via Skip</span>{' '}
                  <button
                    onClick={clearSkips}
                    className="underline text-purple-300 hover:text-purple-200"
                  >
                    (clear)
                  </button>
                </>
              )}
            </span>
          )}
        </div>

        {auditError && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-300">💥 Audit failed: {auditError}</p>
          </div>
        )}

        {/* Orphan groups */}
        {audited && orphans.length === 0 && (
          <div className="p-6 bg-green-500/20 border border-green-500/50 rounded-lg text-center">
            <p className="text-green-300 font-body text-lg">🎉 No orphans found!</p>
            <p className="text-green-400 font-body text-sm mt-2">
              Every polishCatalog entry maps to an existing brand.
            </p>
          </div>
        )}

        {orphans.map((orphan) => {
          const status = statuses[orphan.brandSlug] ?? 'pending';
          const targetId = targets[orphan.brandSlug];
          const target = brands.find((b) => b.id === targetId) ?? null;
          const candidates = candidatesFor(orphan.brandSlug);

          return (
            <div
              key={orphan.brandSlug}
              className={`mb-4 p-4 rounded-lg border ${
                status === 'done'
                  ? 'bg-green-500/10 border-green-500/40'
                  : status === 'running'
                    ? 'bg-blue-500/10 border-blue-500/40'
                    : status === 'error'
                      ? 'bg-red-500/10 border-red-500/40'
                      : 'bg-slate-800/40 border-slate-600'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="font-bold text-glass font-body">
                  Orphan slug:{' '}
                  <span className="font-mono text-purple-300">{orphan.brandSlug}</span>
                  <span className="text-glass-muted text-sm ml-2">
                    · {orphan.count} polish{orphan.count === 1 ? '' : 'es'}
                  </span>
                </p>
                <span className="text-xs text-glass-muted">
                  {status === 'done' && '✅ done'}
                  {status === 'running' && '⏳ running'}
                  {status === 'error' && '💥 error'}
                </span>
              </div>

              {/* Sample polishes for context */}
              {orphan.sampleNames.length > 0 && (
                <div className="text-xs text-glass-muted mb-2 font-body">
                  Sample polishes: {orphan.sampleNames.join(' · ')}
                  {orphan.count > orphan.sampleNames.length &&
                    ` · +${orphan.count - orphan.sampleNames.length} more`}
                </div>
              )}
              {orphan.brandDisplayStrings.length > 0 && (
                <div className="text-xs text-glass-muted mb-3 font-body">
                  Brand strings observed:{' '}
                  {orphan.brandDisplayStrings.map((s) => `"${s}"`).join(', ')}
                </div>
              )}

              {/* Stale-orphan warning. The catalog has entries but every
                  observed brand string slugs to something OTHER than the
                  orphan slug — meaning items have already moved to a
                  correct slug elsewhere; the orphan entries are just
                  leftover stale data. Renaming won't fix it. */}
              {status !== 'done' && isStaleOrphan(orphan) && (
                <div className="text-xs text-amber-300 mb-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded font-body">
                  ⚠️ <strong>Stale orphan</strong> — items already have a brand
                  whose correct slug is{' '}
                  <span className="font-mono">
                    {slugify(orphan.brandDisplayStrings[0])}
                  </span>
                  , not <span className="font-mono">{orphan.brandSlug}</span>.
                  These catalog entries are leftover. Renaming won&apos;t help.
                  Skip for now — a Cloud Function for direct catalog cleanup
                  is the right fix.
                </div>
              )}

              {status !== 'done' && (
                <>
                  <div className="mb-2">
                    <input
                      type="text"
                      value={brandFilters[orphan.brandSlug] || ''}
                      onChange={(e) =>
                        setBrandFilters((bf) => ({
                          ...bf,
                          [orphan.brandSlug]: e.target.value,
                        }))
                      }
                      placeholder="Filter target brand by name or slug…"
                      className="input-glass text-sm w-full"
                    />
                  </div>

                  <div className="space-y-1 max-h-48 overflow-y-auto mb-3">
                    {candidates.length === 0 && (
                      <p className="text-xs text-glass-muted italic py-2 font-body">
                        No candidate brands. Type a search to see all brands.
                      </p>
                    )}
                    {candidates.map((b) => {
                      const isPicked = b.id === targetId;
                      const badge =
                        b.curationStatus === 'verified'
                          ? '✅ verified'
                          : b.curationStatus === 'needs-review'
                            ? '⚠️ needs-review'
                            : b.curationStatus === 'community'
                              ? 'community'
                              : '(default)';
                      return (
                        <button
                          key={b.id}
                          onClick={() =>
                            setTargets((t) => ({
                              ...t,
                              [orphan.brandSlug]: b.id,
                            }))
                          }
                          className={`w-full flex justify-between items-center text-left text-xs px-3 py-2 rounded border font-body ${
                            isPicked
                              ? 'bg-purple-600/30 border-purple-400'
                              : 'bg-slate-900/60 border-slate-600 hover:bg-slate-700'
                          }`}
                        >
                          <span className="text-glass">
                            {isPicked && '👉 '}
                            {b.name}
                          </span>
                          <span className="text-glass-muted">
                            <span className="font-mono mr-2">{b.slug}</span>
                            {badge}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Preview result (only shown if previewOrphanFn has run for this orphan) */}
                  {previews[orphan.brandSlug] && (
                    <div className="mb-3 p-3 bg-slate-900/60 border border-slate-600 rounded text-xs font-body">
                      <p className="text-glass-secondary mb-1">
                        🔎 Preview — <strong className="text-glass">{previews[orphan.brandSlug].itemCount}</strong>{' '}
                        item{previews[orphan.brandSlug].itemCount === 1 ? '' : 's'} would be renamed
                        {target && (
                          <>
                            {' '}→ <span className="text-purple-300">&quot;{target.name}&quot;</span>
                          </>
                        )}
                      </p>
                      <p className="text-glass-muted mb-1">
                        Brand strings matched:{' '}
                        {previews[orphan.brandSlug].variants.map((v) => `"${v}"`).join(', ')}
                      </p>
                      {previews[orphan.brandSlug].samples.length > 0 && (
                        <div className="text-glass-muted mt-2">
                          Sample items:
                          <ul className="ml-3 mt-1 space-y-0.5">
                            {previews[orphan.brandSlug].samples.map((s, i) => (
                              <li key={i}>
                                <span className="text-glass">{s.polishName}</span>{' '}
                                <span className="text-glass-muted">
                                  (brand: &quot;{s.brand}&quot;, owner: {s.owner.slice(0, 8)}…)
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => previewOrphanFn(orphan)}
                      disabled={previewing[orphan.brandSlug] || status === 'running'}
                      className={`px-4 py-2 rounded font-medium text-white text-sm font-body transition-colors ${
                        previewing[orphan.brandSlug] || status === 'running'
                          ? 'bg-slate-600 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {previewing[orphan.brandSlug] ? 'Scanning…' : '🔎 Preview'}
                    </button>
                    <button
                      onClick={() => applyOrphan(orphan)}
                      disabled={!target || status === 'running'}
                      className={`px-4 py-2 rounded font-medium text-white text-sm font-body transition-colors ${
                        !target || status === 'running'
                          ? 'bg-slate-600 cursor-not-allowed'
                          : 'bg-purple-600 hover:bg-purple-700'
                      }`}
                    >
                      {status === 'running'
                        ? 'Renaming…'
                        : target
                          ? `Rename → "${target.name}"`
                          : 'Pick a target brand to enable'}
                    </button>
                    <button
                      onClick={() => {
                        // Persistent skip — stored in localStorage so this
                        // orphan won't reappear on re-audit.
                        persistSkip(orphan.brandSlug);
                        setStatuses((s) => ({ ...s, [orphan.brandSlug]: 'done' }));
                        setLogs((l) => ({
                          ...l,
                          [orphan.brandSlug]:
                            'Skipped — no rename applied. Hidden from future audits (clear via "Clear skipped" up top).',
                        }));
                      }}
                      className="px-4 py-2 rounded font-medium text-glass-secondary text-sm bg-slate-700 hover:bg-slate-600 font-body"
                    >
                      Skip (hide forever)
                    </button>
                  </div>
                </>
              )}

              {logs[orphan.brandSlug] && (
                <p className="text-xs text-glass-muted mt-2 font-body italic">
                  {logs[orphan.brandSlug]}
                </p>
              )}
            </div>
          );
        })}

        {/* Persistent audit log of past renames (this session + earlier). */}
        {isAdmin && auditEntries.length > 0 && (
          <div className="mt-6 p-4 bg-slate-800/60 border border-slate-600 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-glass-secondary font-body">
                📜 Recent renames ({auditEntries.length})
              </p>
              <button
                onClick={loadAuditLog}
                disabled={loadingAudit}
                className="text-xs text-purple-300 hover:text-purple-200 font-body"
              >
                {loadingAudit ? 'Loading…' : '🔄 refresh'}
              </button>
            </div>
            <div className="space-y-1 text-xs font-body max-h-72 overflow-y-auto">
              {auditEntries.map((e) => {
                const date = e.timestampMs ? new Date(e.timestampMs) : null;
                return (
                  <div
                    key={e.id}
                    className="bg-slate-900/60 px-3 py-2 rounded border border-slate-700"
                  >
                    <div className="flex justify-between text-glass-muted">
                      <span>
                        <span className="font-mono text-purple-300">
                          {e.fromOrphanSlug || '(unknown source)'}
                        </span>
                        {' → '}
                        <span className="text-glass">
                          {e.toBrandName || '(unknown target)'}
                        </span>
                        {e.toBrandSlug && (
                          <span className="font-mono text-glass-muted">
                            {' '}
                            ({e.toBrandSlug})
                          </span>
                        )}
                      </span>
                      <span>
                        <strong className="text-glass">{e.itemCount ?? '?'}</strong> items
                      </span>
                    </div>
                    <div className="text-glass-muted text-[10px] mt-0.5">
                      {date ? date.toLocaleString() : '(no timestamp)'}
                      {e.fromBrandStrings && e.fromBrandStrings.length > 0 && (
                        <>
                          {' · matched: '}
                          {e.fromBrandStrings.map((s) => `"${s}"`).join(', ')}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-glass-muted mt-2 italic">
              Logged in <code className="bg-slate-700 px-1 rounded">brandRenameLog</code> collection (admin-only,
              append-only). Use the to-slug to find the target brand if you ever
              need to reverse a rename.
            </p>
          </div>
        )}

        <div className="mt-6 p-4 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
          <p className="text-yellow-300 text-sm font-body">
            ⚠️ <strong className="text-yellow-200">How this works:</strong>{' '}
            Picking a target and clicking Rename updates user items&apos;
            <code className="bg-slate-700 px-1 rounded mx-1">brand</code> field
            to the canonical brand name. The polishCatalog trigger then
            rebuilds: orphan slugs lose their contributors and get deleted,
            the canonical brand picks up the new entries. Re-running the
            audit after a few minutes should show the orphan count drop.
          </p>
        </div>
      </div>
    </div>
  );
}
